import { EveRoutes } from '@ver-uds/e-verify';
import { EverifyUserRole, MyI9TrainingStatus, User } from '@vis-auth/vis-user-client';
import React from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { AccountType } from 'eve-account-client';
import { Audience, useAuthState } from '@vis-auth/react';
import { useUser } from '../../context/User/UserContext';
import { useAccountQuery } from '../../services';
import { InternalRoutes } from '../../constants';

export interface MainRouteProps extends RouteProps {
  path: string | string[];
  exact?: boolean;
  noMatchingRole?: boolean;
  accountIdForQuery?: string;
  accountTypes?: AccountType[];
}
const MainRoute = ({
  path,
  exact = false,
  noMatchingRole = false,
  accountIdForQuery,
  accountTypes,
  children,
}: MainRouteProps): JSX.Element => {
  const { accountId: userAccountId, myI9TrainingStatus, tutorialComplete, roles } = useUser() as User;
  const accountId = accountIdForQuery || userAccountId?.toString();
  const { isLoading, data: accountData } = useAccountQuery(accountId);
  const accountType = accountData?.type;

  const isManagedChild = accountType === AccountType.CHILD && userAccountId !== accountId;
  const evePlusEnabled = accountData?.myI9?.enabled ?? false;
  const evePlusTrainingNotComplete = myI9TrainingStatus === MyI9TrainingStatus.NOTCOMPLETE;

  const { audience } = useAuthState();

  const isDashboardJWT = audience === Audience.everifyEmail;
  const isEnrollmentJWT = audience === Audience.everifyEnrollment;

  return isLoading ? (
    <div className="margin-top-4 margin-bottom-3">
      Loading… <img src={LoaderIcon} alt="" className="height-2" />
    </div>
  ) : (
    <Route
      path={path}
      exact={exact}
      render={() => {
        // Redirect to Dashboard
        if (isDashboardJWT) {
          return <Redirect to={EveRoutes.DASHBOARD} />;
        }

        // Redirect to enrollment
        if (isEnrollmentJWT) {
          return <Redirect to={EveRoutes.ACCOUNT_ENROLL} />;
        }

        // Redirect to tutorial
        if (!tutorialComplete && !roles?.includes(EverifyUserRole.ENROLLMENT)) {
          return <Redirect to={EveRoutes.TUTORIAL} />;
        }

        // Redirect to eve+ training
        if (evePlusEnabled && evePlusTrainingNotComplete && !isManagedChild) {
          return <Redirect to={EveRoutes.I9_TRAINING} />;
        }

        // Redirect to page not found
        if (noMatchingRole || (accountTypes && (!accountType || !accountTypes.includes(accountType)))) {
          return <Redirect to={InternalRoutes.PAGE_NOT_FOUND} />;
        }

        // Show route
        return children;
      }}
    />
  );
};
export default MainRoute;
